import React from 'react';
import moment from 'moment';
import propTypes from 'prop-types';
import { Colors } from 'works/legacy/lib/style-helper';
import { getLengthInWeeks } from 'works/legacy/services/length-in-weeks-service';

function DataColumn({ label, value, colSpan }) {
  return (
    <td colSpan={colSpan}>
      <label>{label}</label>
      <div className="text-heavy">{value}</div>
    </td>
  );
}

function formatDate(date) {
  return date.format('MM/DD/YYYY');
}

function getOfferPresentationData(application, offer) {
  const { staffingRequest, organization } = application;
  const { name: departamentName, organizationBusiness } =
    staffingRequest.organizationUnit.department;

  const startDate = moment(offer.startDate);
  const endDate = moment(offer.endDate);
  const lengthText = getLengthInWeeks(startDate, endDate);

  return {
    code: staffingRequest.code,
    candidateFullName: application.worksProfile.fullName,
    roleAssignment: `${staffingRequest.role.abbreviation} - ${staffingRequest.staffingRequestType}`,
    rate: `$${offer.billingRegularRate.toFixed(2)}`,
    facility: `${organization.name}, ${organizationBusiness.city}, ${organizationBusiness.stateCode}`,
    unitFacility: `${departamentName}, ${organizationBusiness.name}`,
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
    length: lengthText,
    timeOff: offer.timeoffDescription,
    shiftType: offer.shiftType,
    shiftsPerWeek: offer.shiftsPerWeek,
    hoursPerShift: offer.hoursPerShift,
    extensionOfferReason: offer.extensionReason,
  };
}

export function OfferDataTable({ application, offer, highlightMode }) {
  const {
    code,
    roleAssignment,
    rate,
    facility,
    unitFacility,
    startDate,
    endDate,
    length,
    timeOff,
    shiftType,
    shiftsPerWeek,
    hoursPerShift,
    extensionOfferReason,
  } = getOfferPresentationData(application, offer);

  function getExtraCssClasses() {
    return highlightMode ? 'offer-data-table--with-background' : '';
  }

  return (
    <>
      <div className={`offer-data-table ${getExtraCssClasses()}`}>
        <table>
          <tbody>
            <tr>
              <DataColumn label="ID" value={code} />
              <DataColumn label="Role - Assignment" value={roleAssignment} />
              <DataColumn label="Rate" value={rate} />
            </tr>
            <tr>
              <DataColumn
                colSpan={3}
                label="Facility - City, State"
                value={facility}
              />
            </tr>
            <tr>
              <DataColumn
                colSpan={3}
                label="Unit - Facility"
                value={unitFacility}
              />
            </tr>
            <tr>
              <DataColumn label="Start Date" value={startDate} />
              <DataColumn label="End Date" value={endDate} />
              <DataColumn label="Length" value={length} />
            </tr>
            <tr>
              <DataColumn colSpan={3} label="Time Off" value={timeOff} />
            </tr>
            <tr>
              <DataColumn label="Shift Type" value={shiftType} />
              <DataColumn label="Shifts Per Week" value={shiftsPerWeek} />
              <DataColumn label="Hours Per Shift" value={hoursPerShift} />
            </tr>
            {extensionOfferReason && (
              <tr>
                <DataColumn
                  colSpan={3}
                  label="Reason"
                  value={extensionOfferReason}
                />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <style jsx>{`
        .offer-data-table {
          box-sizing: border-box;
          border-radius: 4px;
          margin-bottom: 16px;
        }
        .offer-data-table--with-background {
          background: ${Colors.steelLightest};
          border: 1px solid ${Colors.steel};
          padding: 16px;
        }

        .offer-data-table table {
          width: 100%;
        }
        :global(.offer-data-table tr:not(:first-child) td) {
          padding-top: 16px;
        }
        :global(.offer-data-table tr:not(:last-child) td) {
          padding-bottom: 16px;
          border-bottom: 1px solid ${Colors.steel};
        }
        :global(.offer-data-table .ant-row) {
          flex-wrap: nowrap;
        }
        :global(.offer-data-table .ant-col:not(:last-child)) {
          margin-right: 16px;
        }
      `}</style>
    </>
  );
}

DataColumn.propTypes = {
  label: propTypes.string,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.node,
  ]),
  colSpan: propTypes.number,
};

OfferDataTable.propTypes = {
  highlightMode: propTypes.bool,
  offer: propTypes.shape({
    id: propTypes.string.isRequired,
    billingRegularRate: propTypes.number,
    startDate: propTypes.string,
    endDate: propTypes.string,
    timeoffDescription: propTypes.string,
    shiftType: propTypes.string,
    shiftsPerWeek: propTypes.number,
    hoursPerShift: propTypes.number,
  }),
  application: propTypes.shape({
    id: propTypes.string.isRequired,
    worksProfile: propTypes.shape({
      id: propTypes.string.isRequired,
      fullName: propTypes.string.isRequired,
    }),
    organization: propTypes.shape({
      id: propTypes.string.isRequired,
      name: propTypes.string,
    }),
    staffingRequest: propTypes.shape({
      id: propTypes.string.isRequired,
      code: propTypes.string.isRequired,
      organizationUnit: propTypes.shape({
        id: propTypes.string.isRequired,
        department: propTypes.shape({
          id: propTypes.string.isRequired,
          name: propTypes.string,
          organizationBusiness: propTypes.shape({
            id: propTypes.string.isRequired,
            name: propTypes.string,
            city: propTypes.string,
            stateCode: propTypes.string,
          }),
        }),
      }),
    }),
  }),
};
